import React from 'react';
import PropTypes from 'prop-types';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Divider from '@mui/material/Divider';
import Modal from '@mui/material/Modal';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import SaveIcon from '@mui/icons-material/Save';
import DeleteIcon from '@mui/icons-material/Delete';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import LoadingButton from '@mui/lab/LoadingButton';
import {
  useSelector,
  useDispatch,
} from 'react-redux';

import {
  Autocomplete,
  Checkbox,
  Chip,
  FormControl,
  FormControlLabel,
  FormGroup,
  FormHelperText,
  FormLabel,
  Grid,
  Switch,
} from '@mui/material';
import {
  Add,
  Close,
} from '@mui/icons-material';
import moment from 'moment';
import Horse from '../../../../../../../components/Horse';
import {
  addHorsePublicShare,
  deleteHorsesPublicShare,
  updateHorsesPublicShare,
} from '../../../../../../../redux/horsePublicShare/actions';
import { NOTE_TYPE } from '../../../../../../../utils/enumHelper';
import HorseNotesSelectPopover from '../HorseNoteSelectPopover';
import TaskDefinitionAutocomplete from '../../../../../../../components/Autocompletes/TaskDefinition';

const modalStyle = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  minWidth: 400,
  bgcolor: 'background.paper',
  p: 4,
  overflow: 'auto',
  maxHeight: '90%',
  display: 'flex',
  flexDirection: 'column',
};

const AddEditModal = ({
  open,
  onClose,
  horseId,
  horsePublicShare,
}) => {
  const dispatch = useDispatch();

  // React state
  const [startDate, setStartDate] = React.useState(moment().startOf('day').toDate());
  const [startDateValidation, setStartDateValidation] = React.useState(null);
  const [endDate, setEndDate] = React.useState(null);
  const [endDateValidation, setEndDateValidation] = React.useState(null);
  const [shareNote, setShareNote] = React.useState('')
  const [sendEmail, setSendEmail] = React.useState(true)
  const [sharedNamesEmails, setSharedNamesEmails] = React.useState({})
  const [sharedNoteTypes, setSharedNoteTypes] = React.useState([])
  const [savingError, setSavingError] = React.useState(null);
  const [showSelectHorseNotes, setShowSelectHorseNotes] = React.useState(false);
  const [sharedNotes, setSharedNotes] = React.useState([])
  const [sharedTaskTypeIds, setSharedTaskTypeIds] = React.useState([])

  // Redux state
  const allEventTemplates = useSelector((state) => state.eventTemplate.eventTemplates);
  const barnHorsesMap = useSelector((state) => state.barn.barnHorsesMap);
  const isAddingHorsePublicShare = useSelector((state) => state.horsePublicShare.isAddingHorsePublicShare);
  const isEditingHorsePublicShare = useSelector((state) => state.horsePublicShare.isEditingHorsePublicShare);
  const isDeletingHorsePublicShare = useSelector((state) => state.horsePublicShare.isDeletingHorsePublicShare);
  const isLoading = isDeletingHorsePublicShare || isAddingHorsePublicShare || isEditingHorsePublicShare

  React.useEffect(() => {
    setStartDate(horsePublicShare
      ? new Date(horsePublicShare.startDate)
      : moment().startOf('day').toDate());
    setStartDateValidation(null);
    setEndDate((horsePublicShare && horsePublicShare.endDate)
      ? new Date(horsePublicShare.endDate)
      : null);
    setEndDateValidation(null);

    setSharedNamesEmails((horsePublicShare && horsePublicShare.sharedNamesEmails)
      ? horsePublicShare.sharedNamesEmails
      : {});

    setShareNote((horsePublicShare && horsePublicShare.shareNote)
      ? horsePublicShare.shareNote
      : '')

    setSharedNoteTypes((horsePublicShare && horsePublicShare.sharedNoteTypes)
      ? horsePublicShare.sharedNoteTypes
      : [])

    setSharedNotes((horsePublicShare && horsePublicShare.sharedNotes)
      ? horsePublicShare.sharedNotes
      : [])

    setSharedTaskTypeIds((horsePublicShare && horsePublicShare.sharedTaskTypeIds)
      ? horsePublicShare.sharedTaskTypeIds
      : []);

    setSavingError(null);
  }, [horsePublicShare, open]);

  const validateFields = () => {
    if (!startDate) {
      setStartDateValidation('Please provide a start date at this location.');
      return false;
    } setStartDateValidation(null);
    if (endDate && endDate.getTime() < startDate.getTime()) {
      setEndDateValidation('End date must be after start date.');
      return false;
    } setEndDateValidation(null);

    return true;
  };

  const handleSave = () => {
    if (!validateFields()) { return; }

    // Adding new location
    if (!horsePublicShare) {
      dispatch(addHorsePublicShare(horseId, {
        startDate,
        endDate,
        shareNote,
        sharedNamesEmails,
        sharedNoteTypes,
        sharedNoteIds: sharedNotes.map((note) => note.id),
        sharedTaskTypeIds,
      })).then(() => {
        onClose();
      }).catch((error) => {
        setSavingError(error.message || 'There was an error attempting to add the horse sharing link. Please contact Questri support.');
        console.error(error);
      });
    } else {
      dispatch(updateHorsesPublicShare(horsePublicShare.id, {
        startDate,
        endDate,
        shareNote,
        sharedNamesEmails,
        sharedNoteTypes,
        sharedNoteIds: sharedNotes.map((note) => note.id),
        sharedTaskTypeIds,
        sendEmail,
      })).then(() => {
        onClose();
      }).catch((error) => {
        setSavingError(error.message || 'There was an error attempting to update the horse sharing link. Please contact Questri support.');
        console.error(error);
      });
    }
  };

  const handleDelete = () => {
    dispatch(deleteHorsesPublicShare(horsePublicShare.id)).then(() => {
      onClose();
    }).catch((error) => {
      setSavingError(error.message || 'There was an error attempting to delete the horse sharing link. Please contact Questri support.');
      console.error(error);
    });
  };

  const handleSharedNotesChange = (event) => {
    if (sharedNoteTypes.find((t) => t === event.target.name)) {
      setSharedNoteTypes(sharedNoteTypes.filter((t) => t !== event.target.name))
    } else {
      setSharedNoteTypes([...sharedNoteTypes, event.target.name])
    }
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      aria-labelledby="modal-add-horse-share-title"
      aria-describedby="modal-add-horse--share-description"
    >
      <Box sx={modalStyle}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            flexWrap: 'wrap',
            justifyContent: 'center',
            mb: 2,
          }}
        >

          <Typography
            id="modal-add-horse-share-title"
            variant="h6"
            component="span"
            align="center"
            sx={{ mr: 2 }}
          >
            {horsePublicShare
              ? 'Edit'
              : 'Add'} Horse Sharing Link
          </Typography>
          {
            horseId && barnHorsesMap[horseId] && <Horse horse={barnHorsesMap[horseId][0].horse} />
          }
        </Box>

        <Grid
          container
          spacing={4}
          sx={{
            flex: 1,
            overflow: 'auto',
          }}
        >
          <Grid item xs={12}>
            <TextField
              id="filled-multiline-flexible"
              label="Sharing Link Message/Note"
              fullWidth
              multiline
              rows={3}
              value={shareNote}
              onChange={(e) => setShareNote(e.target.value)}
              helperText="Anyone who views the horse with this link will see this note/message"
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <LocalizationProvider dateAdapter={AdapterMoment}>
              <DatePicker
                label="Sharing Start Date"
                value={startDate}
                maxDate={endDate || undefined}
                onChange={(newValue) => {
                  setStartDate(moment(newValue.toDate()).startOf('day').toDate());
                }}
                renderInput={(params) => (
                  <TextField
                    required
                    {...params}
                    error={!!startDateValidation}
                    helperText={startDateValidation}
                  />
                )}
              />
            </LocalizationProvider>
          </Grid>
          <Grid item xs={12} sm={6}>
            <LocalizationProvider dateAdapter={AdapterMoment}>
              <DatePicker
                label="Sharing End Date"
                value={endDate}
                minDate={startDate}
                onChange={(newValue) => {
                  setEndDate(newValue.toDate());
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    error={!!endDateValidation}
                    helperText={endDateValidation || 'After this date, the sharing link will no longer work. If left blank, access never ends.'}
                  />
                )}
              />
            </LocalizationProvider>
          </Grid>
          <Grid item xs={12}>
            <Divider sx={{ mb: 1 }} />
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
              }}
            >

              <Typography variant="subtitle1">Share With Name/Emails</Typography>
              <Button
                size="small"
                variant="outlined"
                onClick={() => {
                  setSharedNamesEmails({
                    ...sharedNamesEmails,
                    [`UPDATE Name...${Object.keys(sharedNamesEmails).map(() => '.')}`]: '',
                  });
                }}
                startIcon={<Add />}
              >Add New Name/Email
              </Button>
            </Box>
            <Grid container>
              {
                Object.keys(sharedNamesEmails).map((num) => (
                  <Box
                    key={`name-number-${num}`}
                    component="form"
                    sx={{
                      '& .MuiTextField-root': {
                        m: 2,
                        ml: 0,
                      },
                      display: 'flex',
                      alignItems: 'center',
                    }}
                  >
                    <Autocomplete
                      margin="normal"
                      freeSolo
                      options={[]}
                      variant="outlined"
                      id={`ref-num-name-${num}`}
                      size="small"
                      autoComplete
                      value={num}
                      onBlur={(e) => {
                        const deepCopy = JSON.parse(JSON.stringify(sharedNamesEmails)); // deep copy
                        const currentVal = JSON.parse(JSON.stringify(sharedNamesEmails[num])); // deep copy
                        // first remove old key
                        delete deepCopy[num];

                        setSharedNamesEmails({
                          ...deepCopy,
                          [e.target.value]: currentVal,
                        })
                      }}
                      label="Name"
                      sx={{
                        width: '23ch',
                        mr: 2,
                        ml: 0,
                      }}
                      renderInput={(params) => <TextField {...params} label="Name" />}
                    />
                    <TextField
                      margin="normal"
                      variant="outlined"
                      size="small"
                      id={`ref-num-val-${num}`}
                      value={sharedNamesEmails[num]}
                      onChange={(e) => setSharedNamesEmails({
                        ...sharedNamesEmails,
                        [num]: e.target.value,
                      })}
                      label="Email"
                      sx={{ width: '23ch ' }}
                    />
                    <Button
                      size="small"
                      onClick={() => {
                        const deepCopy = JSON.parse(JSON.stringify(sharedNamesEmails)); // deep copy
                        delete deepCopy[num];
                        setSharedNamesEmails(deepCopy);
                      }}
                      startIcon={<Close color="danger" />}
                    >Remove
                    </Button>
                  </Box>
                ))
              }
            </Grid>
            <FormHelperText>Provide Name/Emails to share this horse link with.
              If you do not provide any emails, you can share the link to the horse profile instead.
            </FormHelperText>
          </Grid>
          <Grid item xs={12}>
            <Divider sx={{ mb: 1 }} />
            <Typography variant="subtitle1">Horse Profile Details to Share</Typography>
            <FormHelperText>
              Horse profile fields (name, DOB, reference numbers, etc) along with horse feed
              is always available when sharing a horse. You can also decide what horse notes and tasks
              you would like show with this shared link.
            </FormHelperText>
          </Grid>

          <Grid item xs={12} md={6}>
            <FormControl sx={{ m: 3 }} component="fieldset" variant="standard">
              <FormLabel component="legend">Share Notes by Note Type</FormLabel>
              <FormGroup>
                {
                  NOTE_TYPE.map((type) => (
                    <FormControlLabel
                      margin="none"
                      key={`note-type-checkbox-${type.enum}`}
                      sx={{ mb: 0 }}
                      control={(
                        <Checkbox
                          checked={!!(sharedNoteTypes && sharedNoteTypes.find((t) => t === type.enum))}
                          onChange={handleSharedNotesChange}
                          name={type.enum}
                        />
                      )}
                      label={`All ${type.label} Notes`}
                    />
                  ))
                }
              </FormGroup>
              <FormHelperText>All notes of the above selected types will be available at this shared link.</FormHelperText>
            </FormControl>
            <HorseNotesSelectPopover
              anchorEl={showSelectHorseNotes}
              open={!!showSelectHorseNotes}
              onClose={() => setShowSelectHorseNotes(null)}
              selectedHorseNotes={sharedNotes || []}
              onChangeSelectedHorseNotes={(horseNotes) => setSharedNotes(horseNotes)}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <FormControl sx={{ m: 3 }} component="fieldset" variant="standard">
              <FormLabel component="legend">Share Specific Selected Notes</FormLabel>

              <Button size="small" sx={{ mb: 1 }} variant="outlined" onClick={(e) => setShowSelectHorseNotes(e.currentTarget)}>Select Specific Notes To Share</Button>
              <Chip label={`${sharedNotes.length} specific selected notes to be shared`} />
            </FormControl>
            <FormControl sx={{ m: 3 }} component="fieldset" variant="standard">
              <FormLabel component="legend">Share Task Records/History</FormLabel>
              <TaskDefinitionAutocomplete
                value={allEventTemplates.filter((et) => !!sharedTaskTypeIds.find((t) => t === et.id))}
                eventTemplates={allEventTemplates}
                onSelect={(e, selected) => { setSharedTaskTypeIds(selected.map((et) => et.id)); }}
                multiple
                includeHorseTaskTypeTagId
                includeBarnTaskTypeTagId={false}
                includeEventTaskTypeTagId={false}
              />
              <FormHelperText>Select the type of tasks you would like to share in this shared profile.</FormHelperText>
            </FormControl>
          </Grid>
        </Grid>

        <Divider sx={{ my: '1rem' }} />
        <Box sx={{ textAlign: 'right' }}>
          {horsePublicShare && (
            <FormControl size="small">
              <FormControlLabel
                control={(
                  <Switch
                    checked={sendEmail}
                    onChange={(e) => {
                      setSendEmail(e.target.checked)
                    }}
                  />
                )}
                label="Send Email To Shared With Emails"
              />
            </FormControl>
          )}
          {savingError && <Typography variant="caption" component="div" color="error" gutterBottom>{savingError}</Typography>}
          {horsePublicShare && <Button onClick={handleDelete} endIcon={<DeleteIcon />} disabled={isLoading} sx={{ float: 'left' }}>Delete</Button>}
          <Button onClick={onClose} disabled={isLoading} sx={{ mr: 2 }}>Cancel</Button>
          <LoadingButton onClick={handleSave} disabled={isLoading} variant="contained" color="primary" loading={isLoading} loadingPosition="start" startIcon={<SaveIcon />}>Save</LoadingButton>
        </Box>
      </Box>
    </Modal>
  );
};

AddEditModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  horseId: PropTypes.string,
  horsePublicShare: PropTypes.object,
};

AddEditModal.defaultProps = {
  horseId: null,
  horsePublicShare: null,
};

export default AddEditModal;
